import DelayedApprovalCard from "@/components/DelayedApprovalCard";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useSort } from "@/lib/sort";
import { SortTh } from "@/components/SortTh";
import { docFileHref } from "@/components/orders/shared";
import { curSym } from "@/lib/company";
import {
  ClipboardCheck, History, Settings2, Check, X, Clock, User, ShieldCheck,
  Package, FolderTree, Contact, Boxes, Users as UsersIcon, Loader2, Eye, ChevronUp, Vote,
  LayoutDashboard, AlertTriangle, Timer, Hourglass, Layers, Flame, FlaskConical, Percent, Undo2,
} from "lucide-react";

const ENTITY_ICON = { product: Package, category: FolderTree, customer: Contact, stock: Boxes, user: UsersIcon };
const RESULT = {
  approved: { label: "Onaylandı", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Reddedildi", cls: "bg-red-100 text-red-600" },
  direct: { label: "Doğrudan", cls: "bg-slate-100 text-slate-600" },
};

function StageBadge({ p }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {p.stage === "peer" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Asistan Oylaması</span>}
      {p.stage === "admin" && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Yönetici Onayı</span>}
      {p.stage !== "peer" && p.stage !== "admin" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{p.approver_role_label || "Amir"} Onayı</span>}
      {p.critical && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-600">KRİTİK · kademeli</span>}
      {p.level > 1 && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">Kademe {p.level}</span>}
    </span>
  );
}

function PeerTally({ p }) {
  const votes = p.peer_votes || [];
  const approves = votes.filter((v) => v.vote === "approve").length;
  const rejects = votes.filter((v) => v.vote === "reject").length;
  const total = (p.peer_voters || []).length;
  const required = p.peer_required || (p.peer_result && p.peer_result.required);
  if (!total && !p.peer_result) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]" data-testid={`peer-tally-${p.id}`}>
      <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700"><Check className="h-3 w-3" /> {approves} onay</span>
      <span className="inline-flex items-center gap-1 rounded-sm bg-red-50 px-1.5 py-0.5 font-semibold text-red-600"><X className="h-3 w-3" /> {rejects} ret</span>
      {total ? <span className="text-slate-400">/ {total} asistan · gerekli {required}</span> : null}
      {votes.length > 0 && <span className="text-slate-400">({votes.map((v) => `${v.name || v.email}: ${v.vote === "approve" ? "✓" : "✗"}`).join(", ")})</span>}
    </div>
  );
}

function DiscountPolicyCard() {
  const [pol, setPol] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/discount-policy").then((r) => setPol(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/discount-policy", pol);
      setPol({ high_discount_threshold_pct: data.high_discount_threshold_pct, audit_all: data.audit_all, require_password_high: data.require_password_high });
      toast.success("İskonto politikası kaydedildi");
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  if (!pol) return null;
  return (
    <div className="space-y-3 rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="discount-policy-card">
      <div className="flex items-center gap-2"><Percent className="h-5 w-5 text-brand-green" /><h2 className="font-heading text-lg font-semibold">Manuel İskonto Politikası</h2></div>
      <p className="text-xs text-slate-500">Belirlediğiniz eşiğin <b>üstündeki</b> iskontolar yalnızca <b>Yönetici</b> onayıyla uygulanabilir (amir→yönetici zinciri). Eşik altı iskontolar yetkili amirlerce onaylanır. Tüm iskonto onayları güvenlik/denetim günlüğüne yazılır. Eşik <b>%100</b> → sınırsız (mevcut davranış).</p>
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-[12px] font-medium text-slate-600">Yüksek iskonto eşiği (%)<input data-testid="discount-threshold-input" type="number" min="0" max="100" step="1" value={pol.high_discount_threshold_pct} onChange={(e) => setPol((p) => ({ ...p, high_discount_threshold_pct: e.target.value }))} className="mt-1 block h-9 w-28 rounded-md border border-slate-300 px-2 text-sm tabular" /></label>
        <label className="flex items-center gap-2 text-[13px] font-medium text-slate-600"><input type="checkbox" data-testid="discount-audit-toggle" checked={!!pol.audit_all} onChange={(e) => setPol((p) => ({ ...p, audit_all: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" /> Tüm iskonto onaylarını günlüğe yaz</label>
        <label className="flex items-center gap-2 text-[13px] font-medium text-slate-600" title="Eşik üstü iskontoda amirin PIN/kartı yetmez; ŞİFRE zorunlu olur (sızmış PIN koruması)"><input type="checkbox" data-testid="discount-stepup-toggle" checked={!!pol.require_password_high} onChange={(e) => setPol((p) => ({ ...p, require_password_high: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" /> Yüksek iskontoda amir ŞİFRESİ zorunlu (step-up)</label>
        <Button data-testid="discount-policy-save" onClick={save} disabled={busy} className="bg-brand-green text-white hover:bg-brand-green-600">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Kaydet</Button>
      </div>
    </div>
  );
}

function RefundPolicyCard() {
  const [pol, setPol] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/sales/refund-policy").then((r) => setPol(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/sales/refund-policy", { require_approval: pol.require_approval, threshold_amount: Number(pol.threshold_amount) || 0 });
      setPol({ require_approval: data.require_approval, threshold_amount: data.threshold_amount });
      toast.success("İade onay politikası kaydedildi");
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  if (!pol) return null;
  return (
    <div className="space-y-3 rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="refund-policy-card">
      <div className="flex items-center gap-2"><Undo2 className="h-5 w-5 text-brand-orange" /><h2 className="font-heading text-lg font-semibold">İade Onay Politikası</h2></div>
      <p className="text-xs text-slate-500">Açıkken, <b>Yönetici olmayan</b> kullanıcıların belirlenen <b>tutar eşiğinin üstündeki</b> iadeleri doğrudan yapılmaz; <b>amir (Bölge Sorumlusu / Yönetici) onayına</b> düşer. Yönetici iadeleri her zaman doğrudan uygulanır. Eşik <b>{curSym()}0</b> ve kapalıysa tüm iadeler doğrudan yapılır (mevcut davranış).</p>
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex items-center gap-2 text-[13px] font-medium text-slate-600"><input type="checkbox" data-testid="refund-approval-toggle" checked={!!pol.require_approval} onChange={(e) => setPol((p) => ({ ...p, require_approval: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" /> Eşik üstü iadeler amir onayına düşsün</label>
        <label className="text-[12px] font-medium text-slate-600">İade onay eşiği ({curSym()})<input data-testid="refund-threshold-input" type="number" min="0" step="1" value={pol.threshold_amount} onChange={(e) => setPol((p) => ({ ...p, threshold_amount: e.target.value }))} className="mt-1 block h-9 w-32 rounded-md border border-slate-300 px-2 text-sm tabular" /></label>
        <Button data-testid="refund-policy-save" onClick={save} disabled={busy} className="bg-brand-green text-white hover:bg-brand-green-600">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Kaydet</Button>
      </div>
    </div>
  );
}

function DiscountHeatmapCard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  useEffect(() => { api.get(`/pos/discount/heatmap?days=${days}`).then((r) => setData(r.data)).catch(() => {}); }, [days]);
  return (
    <div className="space-y-3 rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="discount-heatmap-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Flame className="h-5 w-5 text-brand-orange" /><h2 className="font-heading text-lg font-semibold">İskonto Isı Haritası</h2></div>
        <select data-testid="heatmap-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-8 rounded-md border border-slate-300 bg-white px-2 text-[13px]">
          <option value={7}>Son 7 gün</option><option value={30}>Son 30 gün</option><option value={90}>Son 90 gün</option>
        </select>
      </div>
      <p className="text-xs text-slate-500">Hangi amirin ne kadar iskonto <b>onayladığını</b> gösterir — aşırı indirim eğilimini erken yakalayın. Toplam <b>{data?.total_authorizations ?? 0}</b> onay.</p>
      {(!data || !data.rows.length) ? <p className="py-4 text-center text-sm text-slate-400" data-testid="heatmap-empty">Bu dönemde iskonto onayı kaydı yok.</p> : (
        <div className="overflow-hidden rounded-lg ring-1 ring-inset ring-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr><th className="px-3 py-2">Amir</th><th className="px-3 py-2">Rol</th><th className="px-3 py-2 text-right">Onay</th><th className="px-3 py-2 text-right">Ort. %</th><th className="px-3 py-2 text-right">En yüksek %</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map((r) => { const heat = Math.min(1, r.count / Math.max(1, data.rows[0].count)); return (
                <tr key={r.authorizer} data-testid={`heatmap-row-${r.authorizer}`}>
                  <td className="px-3 py-2 font-semibold text-slate-700">{r.name}</td>
                  <td className="px-3 py-2 text-slate-500">{r.role_label}</td>
                  <td className="px-3 py-2 text-right"><span className="inline-block rounded-full px-2 py-0.5 text-xs font-bold" style={{ backgroundColor: `rgba(234,88,12,${0.12 + heat * 0.5})`, color: heat > 0.5 ? "#7c2d12" : "#9a3412" }}>{r.count}</span></td>
                  <td className="px-3 py-2 text-right tabular text-slate-600">%{r.avg_pct}</td>
                  <td className="px-3 py-2 text-right tabular font-semibold text-slate-800">%{r.max_pct}</td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WorkflowThresholdCard() {
  const [thr, setThr] = useState("");
  const [def, setDef] = useState(25000);
  const [saving, setSaving] = useState(false);
  const [reqApproval, setReqApproval] = useState(true);
  const [multiStep, setMultiStep] = useState(true);
  const [featBusy, setFeatBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [wRole, setWRole] = useState("");
  const [wThr, setWThr] = useState("");
  const [wAmt, setWAmt] = useState("");
  const [whatIf, setWhatIf] = useState(null);
  const loadPreview = () => api.get("/workflow-config/chain-preview").then((r) => setPreview(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/workflow-config").then((r) => { setThr(String(r.data.chain_threshold ?? "")); setDef(r.data.default_threshold ?? 25000); }).catch(() => {});
    api.get("/features").then((r) => { const f = r.data.features || {}; setReqApproval(f["flow.require_order_approval"] !== false); setMultiStep(f["flow.multi_step_approval"] !== false); }).catch(() => {});
    loadPreview();
  }, []);
  useEffect(() => { if (preview && !wRole) { setWRole(preview.role || "cashier"); setWThr(String(preview.threshold ?? "")); } }, [preview]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!preview) return;
    const params = {};
    if (wRole) params.role = wRole;
    if (wThr !== "") params.threshold = Number(wThr);
    if (wAmt !== "") params.amount = Number(wAmt);
    const id = setTimeout(() => { api.get("/workflow-config/chain-preview", { params }).then((r) => setWhatIf(r.data)).catch(() => {}); }, 250);
    return () => clearTimeout(id);
  }, [wRole, wThr, wAmt, preview]);
  const save = async () => {
    const v = Number(thr);
    if (!(v >= 0)) return toast.error("Geçerli bir eşik tutarı girin");
    setSaving(true);
    try { const { data } = await api.put("/workflow-config", { chain_threshold: v }); setThr(String(data.chain_threshold)); toast.success("İş akışı eşiği kaydedildi"); loadPreview(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setSaving(false);
  };
  const setFeature = async (key, val, setter) => {
    setter(val); setFeatBusy(true);
    try { await api.put("/settings/features", { features: { [key]: val } }); toast.success("Onay akışı güncellendi"); loadPreview(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setter(!val); }
    setFeatBusy(false);
  };
  const chip = (l, i) => <span key={i} className="mx-0.5 inline-block rounded bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">{l}</span>;
  return (
    <div data-testid="workflow-threshold-card" className="space-y-4 rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200">
      <div className="flex items-center gap-2"><Layers className="h-5 w-5 text-brand-green" /><h2 className="font-heading text-lg font-semibold">İş Akışı Onay Zinciri</h2><span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-green">tek merkez</span></div>
      <p className="text-xs text-slate-500">Tüm sipariş/transfer onay kuralları <b>BURADAN</b> yönetilir. Onay açıkken işlemler rol hiyerarşisine göre ilerler; <b>eşik altı</b> tek amir, <b>eşik üstü</b> nihai Yönetici'ye kadar (çok kademe). Çok kademe kapatılırsa her tutar tek amir onayıyla geçer.</p>

      <div className="grid gap-2 sm:grid-cols-2">
        <label data-testid="wf-require-approval-row" className={`flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2.5 ring-1 ring-inset transition-colors ${reqApproval ? "bg-emerald-50/50 ring-emerald-200" : "bg-slate-50 ring-slate-200"}`}>
          <span className="text-sm font-medium text-slate-700">Sipariş/transfer onayı gerekli</span>
          <input type="checkbox" data-testid="wf-require-approval" disabled={featBusy} checked={reqApproval} onChange={(e) => setFeature("flow.require_order_approval", e.target.checked, setReqApproval)} className="h-4 w-4 rounded border-slate-300" />
        </label>
        <label data-testid="wf-multistep-row" className={`flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2.5 ring-1 ring-inset transition-colors ${multiStep && reqApproval ? "bg-emerald-50/50 ring-emerald-200" : "bg-slate-50 ring-slate-200"} ${!reqApproval ? "opacity-50" : ""}`}>
          <span className="text-sm font-medium text-slate-700">Çok kademeli zincir (amir→…→Yönetici)</span>
          <input type="checkbox" data-testid="wf-multistep" disabled={featBusy || !reqApproval} checked={multiStep} onChange={(e) => setFeature("flow.multi_step_approval", e.target.checked, setMultiStep)} className="h-4 w-4 rounded border-slate-300" />
        </label>
      </div>

      <div className={`flex flex-wrap items-end gap-3 ${(!reqApproval || !multiStep) ? "opacity-50" : ""}`}>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Tam zincir eşiği ({curSym()}, KDV hariç)</label>
          <div className="relative w-48">
            <span className="pointer-events-none absolute left-2.5 top-1/2 z-10 -translate-y-1/2 text-sm font-semibold text-slate-500">{curSym()}</span>
            <input data-testid="workflow-threshold-input" type="number" min="0" step="500" value={thr} onChange={(e) => setThr(e.target.value)} disabled={!reqApproval || !multiStep} className="h-10 w-48 rounded-md border border-slate-300 pl-7 pr-3 text-sm tabular" placeholder={String(def)} />
          </div>
        </div>
        <Button data-testid="workflow-threshold-save" onClick={save} disabled={saving || !reqApproval || !multiStep} className="bg-brand-green text-white hover:bg-brand-green-600">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Kaydet</Button>
        <button type="button" data-testid="workflow-threshold-default" onClick={() => setThr(String(def))} className="text-xs font-semibold text-slate-500 hover:text-slate-800">Varsayılana dön ({def.toLocaleString("tr-TR")} {curSym()})</button>
      </div>

      {preview && (
        <div data-testid="wf-chain-preview" className="rounded-lg bg-slate-50 p-3 text-[12px] ring-1 ring-inset ring-slate-200">
          <p className="mb-1.5 font-semibold text-slate-600">Canlı önizleme — örnek: <b>{preview.role_label}</b>'in oluşturduğu işlem</p>
          {!preview.require_approval ? (
            <p className="text-slate-500">Onay <b>kapalı</b> — işlemler onaysız anında tamamlanır.</p>
          ) : (
            <div className="space-y-1 text-slate-600">
              <p><span className="text-slate-400">Eşik altı:</span> {preview.below.length ? preview.below.map(chip) : <span className="italic text-slate-400">onaysız</span>}</p>
              <p><span className="text-slate-400">Eşik üstü:</span> {preview.above.length ? preview.above.map(chip) : <span className="italic text-slate-400">onaysız</span>}</p>
            </div>
          )}
        </div>
      )}

      {preview && (
        <div data-testid="wf-whatif" className="rounded-lg border border-dashed border-brand-green/40 bg-emerald-50/40 p-3">
          <p className="mb-2 flex items-center gap-1.5 text-[12px] font-bold text-emerald-800"><FlaskConical className="h-3.5 w-3.5" /> Zinciri Dene — "ne olurdu?" (kaydetmeden dener)</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-[11px] font-medium text-slate-500">Oluşturan rol</label>
              <select data-testid="wf-whatif-role" value={wRole} onChange={(e) => setWRole(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
                {(preview.roles || []).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-slate-500">Deneme eşiği ({curSym()})</label>
              <input data-testid="wf-whatif-threshold" type="number" min="0" step="500" value={wThr} onChange={(e) => setWThr(e.target.value)} className="h-9 w-32 rounded-md border border-slate-300 px-2 text-sm tabular" placeholder={String(def)} />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-slate-500">İşlem tutarı ({curSym()})</label>
              <input data-testid="wf-whatif-amount" type="number" min="0" step="500" value={wAmt} onChange={(e) => setWAmt(e.target.value)} className="h-9 w-32 rounded-md border border-slate-300 px-2 text-sm tabular" placeholder="örn. 50000" />
            </div>
          </div>
          {whatIf && (
            <div data-testid="wf-whatif-result" className="mt-3 space-y-1 text-[12px] text-slate-600">
              {!whatIf.require_approval ? <p className="text-slate-500">Onay <b>kapalı</b> — işlemler onaysız anında tamamlanır.</p> : (
                <>
                  {whatIf.chain !== undefined && (
                    <p><span className="font-semibold text-emerald-700">{Number(whatIf.amount || 0).toLocaleString("tr-TR")} {curSym()} işlemde ({whatIf.depth} kademe):</span> {whatIf.chain.length ? whatIf.chain.map(chip) : <span className="italic text-slate-400">onaysız</span>}</p>
                  )}
                  <p><span className="text-slate-400">Eşik altı:</span> {whatIf.below.length ? whatIf.below.map(chip) : <span className="italic text-slate-400">onaysız</span>}</p>
                  <p><span className="text-slate-400">Eşik üstü:</span> {whatIf.above.length ? whatIf.above.map(chip) : <span className="italic text-slate-400">onaysız</span>}</p>
                  <p className="text-[11px] text-slate-400">Deneme eşiği: <b>{Number(whatIf.threshold || 0).toLocaleString("tr-TR")} {curSym()}</b> · Çok kademe {whatIf.multi_step ? "açık" : "kapalı"}</p>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HeatmapCard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/approvals/heatmap").then((r) => setData(r.data)).catch(() => {}); }, []);
  const tl = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + curSym();
  const dur = (h) => (h >= 24 ? `${Math.floor(h / 24)}g ${Math.round(h % 24)}s` : `${h}s`);
  if (!data) return <div className="rounded-md bg-white p-10 text-center text-sm text-slate-400 ring-1 ring-inset ring-slate-200">Yükleniyor…</div>;
  if (!data.rows.length) return <div data-testid="heatmap-empty" className="rounded-md bg-white p-10 text-center text-sm text-slate-400 ring-1 ring-inset ring-slate-200">Onay bekleyen çok kademeli sipariş/transfer yok.</div>;
  return (
    <div data-testid="approval-heatmap" className="space-y-3">
      <p className="text-xs text-slate-500">Onay bekleyen çok kademeli sipariş/transferler, <b>şu an hangi kademede</b> beklediklerine göre gruplanır. <b>{data.overdue_hours}</b> saat+ bekleyenler <span className="font-semibold text-red-600">geciken</span> sayılır. <span className="text-brand-green">Bir satıra tıklayın → o kademedeki bekleyen onaylar Transferler ekranında açılır.</span></p>
      <div className="grid grid-cols-2 gap-3 sm:max-w-md" data-testid="heatmap-kpis">
        <div data-testid="heatmap-kpi-waiting" className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bekleyen Onay</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{data.total}</p>
        </div>
        <div data-testid="heatmap-kpi-overdue" className={`rounded-lg p-4 ring-1 ring-inset ${data.rows.reduce((a, r) => a + r.overdue, 0) > 0 ? "bg-red-50 ring-red-200" : "bg-white ring-slate-200"}`}>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Geciken (&gt;{data.overdue_hours} saat)</p>
          <p className={`mt-1 text-2xl font-bold ${data.rows.reduce((a, r) => a + r.overdue, 0) > 0 ? "text-red-600" : "text-slate-800"}`}>{data.rows.reduce((a, r) => a + r.overdue, 0)}</p>
        </div>
      </div>
      <div className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
            <th className="px-4 py-2.5 font-medium">Onay Kademesi (rol)</th>
            <th className="px-4 py-2.5 text-right font-medium">Bekleyen</th>
            <th className="px-4 py-2.5 text-right font-medium">Geciken</th>
            <th className="px-4 py-2.5 text-right font-medium">En Eski</th>
            <th className="px-4 py-2.5 text-right font-medium">Toplam Tutar</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((r, i) => {
              const hot = r.overdue > 0;
              return (
                <tr key={i} data-testid={`heatmap-row-${i}`} onClick={() => navigate("/transferler?mine=1")} title="Bu kademedeki bekleyen onayları aç" className={`cursor-pointer transition-colors hover:bg-brand-green/5 ${hot ? "bg-red-50/50" : ""}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800"><span className="inline-flex items-center gap-1.5">{r.level}<span className="text-[10px] font-normal text-brand-green">aç →</span></span></td>
                  <td className="px-4 py-3 text-right tabular"><span className="inline-flex items-center justify-end gap-2"><span className="hidden h-1.5 rounded-full bg-brand-green/40 sm:inline-block" style={{ width: `${Math.max(6, Math.round((r.count / Math.max(1, ...data.rows.map((x) => x.count))) * 56))}px` }} />{r.count}</span></td>
                  <td className={`px-4 py-3 text-right tabular font-bold ${hot ? "text-red-600" : "text-slate-300"}`}>{r.overdue}</td>
                  <td className="px-4 py-3 text-right tabular">{dur(r.oldest_hours)}</td>
                  <td className="px-4 py-3 text-right tabular text-slate-600">{tl(r.total_amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApprovalDetail({ detail }) {
  if (!detail || ((!detail.rows || detail.rows.length === 0) && (!detail.count_rows || detail.count_rows.length === 0)))
    return <div className="border-t border-slate-100 px-4 py-3 text-xs text-slate-400">Görüntülenecek detay yok.</div>;
  const { mode, rows = [], count_rows } = detail;
  const isCount = mode === "count" && Array.isArray(count_rows);
  const title = isCount ? "Sayım farkı kalemleri (sistem → sayılan)" : mode === "update" ? "Değişiklikler (eski → yeni)" : mode === "delete" ? "Silinecek kayıt bilgileri" : "Eklenecek bilgiler";
  return (
    <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3" data-testid="approval-detail">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{title}</p>
      {rows.length > 0 && (
        <div className="overflow-hidden rounded-md bg-white ring-1 ring-inset ring-slate-200">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={i} className={mode === "update" && r.changed ? "bg-amber-50/60" : ""}>
                  <td className="w-44 px-3 py-2 align-top text-xs font-medium text-slate-500">{r.label}</td>
                  <td className="px-3 py-2">
                    {mode === "update" ? (
                      r.changed ? (
                        <span><span className="text-slate-400 line-through">{r.before}</span><span className="mx-2 text-slate-300">→</span><span className="font-semibold text-emerald-700">{r.after}</span></span>
                      ) : (<span className="text-slate-500">{r.after}</span>)
                    ) : (<span className="text-slate-700">{r.after}</span>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isCount && count_rows.length > 0 && (
        <div className="mt-2 overflow-hidden rounded-md bg-white ring-1 ring-inset ring-slate-200" data-testid="approval-count-rows">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
              <th className="px-3 py-2 font-medium">Ürün</th>
              <th className="px-3 py-2 text-right font-medium">Sistem</th>
              <th className="px-3 py-2 text-right font-medium">Sayılan</th>
              <th className="px-3 py-2 text-right font-medium">Fark</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {count_rows.map((r, i) => (
                <tr key={i} data-testid={`approval-count-row-${i}`} className={r.diff < 0 ? "bg-red-50/40" : "bg-emerald-50/40"}>
                  <td className="px-3 py-2 font-medium text-slate-700">{r.name}{r.sku ? <span className="ml-1 text-[10px] text-slate-400">{r.sku}</span> : null}</td>
                  <td className="px-3 py-2 text-right tabular text-slate-600">{r.system}</td>
                  <td className="px-3 py-2 text-right tabular font-semibold text-slate-800">{r.counted}</td>
                  <td className={`px-3 py-2 text-right tabular font-bold ${r.diff < 0 ? "text-red-600" : "text-emerald-700"}`}>{r.diff > 0 ? "+" : ""}{r.diff}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isCount && count_rows.length === 0 && (
        <div className="mt-1 text-xs text-slate-400">Fark içeren kalem yok (tüm sayımlar sistemle eşleşiyor).</div>
      )}
      {isCount && detail.document_url && (
        <div className="mt-2">
          <a data-testid="approval-count-doc" href={docFileHref(detail.document_url)} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-brand-green ring-1 ring-inset ring-brand-green/40 hover:bg-brand-green/5">
            <Eye className="h-3.5 w-3.5" /> Yüklenen Sayım Belgesini Aç / İncele
          </a>
        </div>
      )}
    </div>
  );
}

export default function Approvals() {
  const { can } = useAuth();
  const isAdmin = can("approve.admin");
  const canApprove = can("approve.admin") || can("approve.chain");
  const [tab, setTab] = useState(canApprove ? "board" : "history");
  const [pending, setPending] = useState([]);
  const [summary, setSummary] = useState(null);
  const [audit, setAudit] = useState([]);
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const [config, setConfig] = useState(null);
  const [matrixRole, setMatrixRole] = useState("");
  const [busy, setBusy] = useState(null);
  const [openRows, setOpenRows] = useState({});
  const [histScope, setHistScope] = useState("today");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadPending = useCallback(() => {
    if (!canApprove) return;
    api.get("/approvals?status=pending").then((r) => setPending(r.data)).catch(() => {});
  }, [canApprove]);
  const loadSummary = useCallback(() => {
    if (!canApprove) return;
    api.get("/approvals/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [canApprove]);
  const loadAudit = useCallback(() => api.get("/audit-log").then((r) => setAudit(r.data)).catch(() => {}), []);
  const loadConfig = useCallback(() => api.get("/approvals/config").then((r) => setConfig(r.data)).catch(() => {}), []);

  useEffect(() => { loadPending(); loadSummary(); loadAudit(); if (isAdmin) loadConfig(); }, [loadPending, loadSummary, loadAudit, loadConfig, isAdmin]);
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const decide = async (p, action) => {
    setBusy(p.id + action);
    try {
      const { data } = await api.post(`/approvals/${p.id}/${action}`, {});
      if (data?.stage === "peer") toast.success("Oyunuz kaydedildi");
      else if (data?.stage === "advanced") toast.success("Çoğunluk sağlandı · Yönetici nihai onayına iletildi");
      else if (data?.stage === "failed") toast.warning("Çoğunluk sağlanamadı · işlem iptal edildi (Yönetici'ye rapor gönderildi)");
      else if (data?.stage === "escalated") toast.success(data.message || "Onaylandı ve bir üst kademeye iletildi");
      else toast.success(action === "approve" ? "Onaylandı ve uygulandı" : "Reddedildi");
      loadPending(); loadSummary(); loadAudit();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(null);
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSelected = pending.length > 0 && selected.size === pending.length;
  const toggleAll = () => setSelected((s) => (s.size === pending.length ? new Set() : new Set(pending.map((p) => p.id))));
  // Toplu onay/ret: mevcut tekli endpoint'i SIRAYLA çağırır (workflow/peer/escalation kuralları aynen uygulanır).
  const bulkDecide = async (action) => {
    const ids = pending.filter((p) => selected.has(p.id)).map((p) => p.id);
    if (ids.length === 0) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.post(`/approvals/${id}/${action}`, {}); ok++; } catch { fail++; }
    }
    setBulkBusy(false); setSelected(new Set());
    if (ok) toast.success(`${ok} işlem ${action === "approve" ? "onaylandı" : "reddedildi"}${fail ? ` · ${fail} başarısız` : ""}`);
    else toast.error(`${fail} işlem işlenemedi`);
    loadPending(); loadSummary(); loadAudit();
  };

  const toggleRole = (code) => setConfig((c) => ({ ...c, gated_roles: c.gated_roles.includes(code) ? c.gated_roles.filter((r) => r !== code) : [...c.gated_roles, code] }));
  const toggleMatrix = (role, action) => setConfig((c) => {
    const m = { ...(c.action_matrix || {}) };
    const rm = { ...(m[role] || {}) };
    rm[action] = !rm[action];
    m[role] = rm;
    return { ...c, action_matrix: m };
  });
  const saveConfig = async () => {
    setBusy("cfg");
    try { const { data } = await api.put("/approvals/config", { enabled: config.enabled, gated_roles: config.gated_roles, action_matrix: config.action_matrix || {} }); setConfig((c) => ({ ...c, ...data })); toast.success("Onay ayarları kaydedildi"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(null);
  };

  const dt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");
  const ageLbl = (h) => (h >= 24 ? `${Math.floor(h / 24)}g ${Math.round(h % 24)}s` : `${h} saat`);
  const _todayStr = new Date().toISOString().slice(0, 10);
  const shownAudit = sortRows(audit.filter((a) => (histScope === "today" ? (a.at || "").slice(0, 10) === _todayStr : (a.at || "").slice(0, 10) < _todayStr)), {
    action: (a) => a.action_label || "",
    target: (a) => a.target_label || "",
    actor: (a) => a.actor || "",
    result: (a) => a.result || "",
    approver: (a) => a.approver || "",
    at: (a) => a.at || "",
  });
  const auditPaged = usePaged(shownAudit);

  const TabBtn = ({ id, icon: Icon, label, count, warn }) => (
    <button data-testid={`appr-tab-${id}`} onClick={() => setTab(id)}
      className={`inline-flex items-center gap-1.5 rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${tab === id ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>
      <Icon className="h-4 w-4" /> {label}
      {count > 0 && <span className={`ml-1 rounded-full px-1.5 text-[10px] ${warn ? "bg-red-500 text-white" : tab === id ? "bg-white/25" : "bg-brand-orange text-white"}`}>{count}</span>}
    </button>
  );

  const StatCard = ({ testid, icon: Icon, label, value, tone }) => (
    <div data-testid={testid} className={`rounded-lg p-4 ring-1 ring-inset ${tone === "danger" ? "bg-red-50 ring-red-200" : tone === "warn" ? "bg-amber-50 ring-amber-200" : "bg-white ring-slate-200"}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <Icon className={`h-4 w-4 ${tone === "danger" ? "text-red-500" : tone === "warn" ? "text-amber-500" : "text-brand-green"}`} />
      </div>
      <p className={`mt-1 font-heading text-3xl font-bold ${tone === "danger" ? "text-red-600" : "text-slate-900"}`}>{value}</p>
    </div>
  );

  return (
    <div className="space-y-5 fade-in" data-testid="approvals-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Denetim</p><h1 className="font-heading text-2xl font-bold text-slate-900">Onaylar & İşlem Geçmişi</h1></div>
        </div>
        <div className="inline-flex flex-wrap rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200">
          {canApprove && <TabBtn id="board" icon={LayoutDashboard} label="Onay Panosu" count={summary?.overdue || 0} warn />}
          {canApprove && <TabBtn id="pending" icon={Clock} label="Bekleyen Onaylar" count={pending.length} />}
          {canApprove && <TabBtn id="heatmap" icon={Flame} label="Isı Haritası" count={summary?.overdue || 0} warn />}
          <TabBtn id="history" icon={History} label="İşlem Geçmişi" />
          {isAdmin && <TabBtn id="settings" icon={Settings2} label="Onay Ayarları" />}
        </div>
      </div>

      {tab === "board" && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard testid="summary-total" icon={Hourglass} label="Bekleyen Onay" value={summary.total} />
            <StatCard testid="summary-overdue" icon={AlertTriangle} label={`Geciken (>${summary.overdue_hours} saat)`} value={summary.overdue} tone={summary.overdue > 0 ? "danger" : "default"} />
            <StatCard testid="summary-peer" icon={Vote} label="Asistan Oylamasında" value={summary.peer_pending} tone={summary.peer_pending > 0 ? "warn" : "default"} />
            <StatCard testid="summary-actions" icon={Timer} label="İşlem Türü" value={summary.by_action.length} />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2 overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-slate-200">
              <div className="border-b border-slate-100 px-4 py-3"><p className="font-heading font-semibold text-slate-800">En eski bekleyenler · kim neyi beklettiği</p></div>
              {summary.oldest.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-slate-400">Bekleyen onay yok. 🎉</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {summary.oldest.map((o) => (
                    <div key={o.id} data-testid="summary-oldest-row" className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">{o.action_label} <span className="font-normal text-slate-400">· {o.target_label || "—"}</span></p>
                        <p className="truncate text-xs text-slate-500">
                          <User className="mr-1 inline h-3 w-3" />{o.requested_by} <span className="rounded-sm bg-slate-100 px-1 text-[10px]">{o.requested_role_label}</span>
                          <span className="mx-1 text-slate-300">→ bekleyen:</span><span className="font-medium text-slate-600">{o.stage === "peer" ? "Asistan oylaması" : o.approver_role_label}</span>
                        </p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${o.overdue ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-600"}`}>{ageLbl(o.age_hours)}</span>
                    </div>
                  ))}
                </div>
              )}
              {summary.total > 0 && <button data-testid="board-goto-pending" onClick={() => setTab("pending")} className="w-full border-t border-slate-100 px-4 py-2.5 text-center text-sm font-semibold text-brand-green hover:bg-slate-50">Bekleyen onayları aç →</button>}
            </div>

            <div className="space-y-4">
              <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">İşlem türüne göre</p>
                {summary.by_action.length === 0 ? <p className="text-sm text-slate-400">—</p> : summary.by_action.map((a) => (
                  <div key={a.label} className="flex items-center justify-between py-1 text-sm"><span className="text-slate-600">{a.label}</span><span className="rounded-sm bg-slate-100 px-1.5 text-xs font-semibold text-slate-600">{a.count}</span></div>
                ))}
              </div>
              <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Talep edene göre</p>
                {summary.by_requester.length === 0 ? <p className="text-sm text-slate-400">—</p> : summary.by_requester.map((a) => (
                  <div key={a.label} className="flex items-center justify-between py-1 text-sm"><span className="truncate text-slate-600">{a.label}</span><span className="rounded-sm bg-slate-100 px-1.5 text-xs font-semibold text-slate-600">{a.count}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "pending" && (
        <div className="space-y-2">
          {pending.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-md bg-white px-3 py-2 ring-1 ring-inset ring-slate-200" data-testid="appr-bulk-bar">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <input type="checkbox" data-testid="appr-select-all" checked={allSelected} onChange={toggleAll} className="h-4 w-4 rounded border-slate-300" />
                Tümünü seç
              </label>
              <span className="text-xs text-slate-400" data-testid="appr-selected-count">{selected.size} seçili</span>
              {selected.size > 0 && (
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <Button data-testid="appr-bulk-reject" onClick={() => bulkDecide("reject")} disabled={bulkBusy} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50">{bulkBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <X className="mr-1 h-4 w-4" />} Seçilenleri Reddet</Button>
                  <Button data-testid="appr-bulk-approve" onClick={() => bulkDecide("approve")} disabled={bulkBusy} size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700">{bulkBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />} Seçilenleri Onayla</Button>
                  <button type="button" data-testid="appr-bulk-clear" onClick={() => setSelected(new Set())} className="text-xs font-semibold text-slate-400 hover:text-slate-700">Temizle</button>
                </div>
              )}
            </div>
          )}
          {pending.length === 0 && <div className="rounded-md bg-white p-10 text-center text-sm text-slate-400 ring-1 ring-inset ring-slate-200">Bekleyen onay yok.</div>}
          {pending.map((p) => {
            const Icon = ENTITY_ICON[p.entity] || Package;
            const isOpen = !!openRows[p.id];
            const isPeer = p.stage === "peer";
            return (
              <div key={p.id} data-testid={`pending-row-${p.id}`} className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="flex items-center gap-3">
                    <input type="checkbox" data-testid={`appr-select-${p.id}`} checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 shrink-0 rounded border-slate-300" />
                    <div className={`flex h-10 w-10 items-center justify-center rounded-md ${isPeer ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>{isPeer ? <Vote className="h-5 w-5" /> : <Icon className="h-5 w-5" />}</div>
                    <div>
                      <p className="flex flex-wrap items-center gap-2 font-semibold text-slate-800">{p.action_label} <span className="text-slate-400">·</span> <span className="font-normal">{p.target_label || "—"}</span> <StageBadge p={p} /></p>
                      <p className="text-xs text-slate-500"><User className="mr-1 inline h-3 w-3" />{p.requested_by} <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{p.requested_role_label}</span> · {dt(p.created_at)}</p>
                      <PeerTally p={p} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`detail-btn-${p.id}`} onClick={() => setOpenRows((o) => ({ ...o, [p.id]: !o[p.id] }))} variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50">{isOpen ? <ChevronUp className="mr-1 h-4 w-4" /> : <Eye className="mr-1 h-4 w-4" />} {isOpen ? "Gizle" : "Detay"}</Button>
                    <Button data-testid={`reject-btn-${p.id}`} onClick={() => decide(p, "reject")} disabled={!!busy} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">{busy === p.id + "reject" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <X className="mr-1 h-4 w-4" />} {isPeer ? "Ret Oyu" : "Reddet"}</Button>
                    <Button data-testid={`approve-btn-${p.id}`} onClick={() => decide(p, "approve")} disabled={!!busy} className="bg-emerald-600 text-white hover:bg-emerald-700">{busy === p.id + "approve" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />} {isPeer ? "Onay Oyu" : (p.critical && !isAdmin ? "Onayla & Üst Kademeye İlet" : "Onayla & Uygula")}</Button>
                  </div>
                </div>
                {isOpen && <ApprovalDetail detail={p.detail} />}
              </div>
            );
          })}
        </div>
      )}

      {tab === "history" && (
        <div className="space-y-3">
          <div className="inline-flex rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200">
            {[["today", "Bugün"], ["archive", "Arşiv"]].map(([k, l]) => (
              <button key={k} data-testid={`hist-scope-${k}`} onClick={() => setHistScope(k)}
                className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${histScope === k ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
            ))}
          </div>
          <div className="overflow-hidden rounded-md bg-white ring-1 ring-inset ring-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr><SortTh label="İşlem" k="action" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="audit-sort-action" /><SortTh label="Hedef" k="target" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="audit-sort-target" /><SortTh label="Yapan" k="actor" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="audit-sort-actor" /><SortTh label="Sonuç" k="result" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="audit-sort-result" /><SortTh label="Onaylayan" k="approver" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="audit-sort-approver" /><SortTh label="Tarih" k="at" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="audit-sort-at" /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shownAudit.length === 0 ? (<tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">{histScope === "today" ? "Bugün kayıt yok." : "Arşivde kayıt yok."}</td></tr>) : auditPaged.slice.map((a) => {
                const r = RESULT[a.result] || RESULT.direct;
                return (
                  <tr key={a.id} data-testid="audit-row" className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-700">{a.action_label}</td>
                    <td className="px-4 py-3 text-slate-600">{a.target_label || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{a.actor} <span className="text-[10px] text-slate-400">({a.actor_role_label})</span></td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.cls}`}>{r.label}</span>{a.note ? <span className="ml-1 text-[10px] text-slate-400">{a.note}</span> : null}</td>
                    <td className="px-4 py-3 text-slate-500">{a.approver || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{dt(a.at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <Pagination page={auditPaged.page} pageSize={auditPaged.pageSize} total={auditPaged.total}
            onPage={auditPaged.setPage} onPageSize={auditPaged.setPageSize} testid="audit-pagination" />
        </div>
      )}

      {tab === "heatmap" && <HeatmapCard />}
      {tab === "settings" && config && isAdmin && (
        <div className="max-w-3xl space-y-4">
        <div className="space-y-4 rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brand-green" /><h2 className="font-heading text-lg font-semibold">Onay Kuyruğu Ayarları</h2></div>
          <label className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm">
            <input type="checkbox" data-testid="appr-enabled-toggle" checked={config.enabled} onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" />
            Onay kuyruğu aktif (kapalıysa hiçbir işlem onaya düşmez)
          </label>
          <div>
            <p className="mb-2 text-sm font-semibold text-slate-700">Onaya tabi roller</p>
            <p className="mb-2 text-xs text-slate-500">Seçili rollerin ürün / kategori / stok / müşteri değişiklikleri doğrudan amirlerinin onayına düşer. Yönetici Asistanı'nın değişiklikleri önce diğer asistanların oylamasına, çoğunluk sağlanırsa Yönetici onayına gider.</p>
            <div className="grid grid-cols-2 gap-2">
              {config.eligible_roles.map((r) => (
                <label key={r.code} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm ring-1 ring-inset ring-slate-200">
                  <input type="checkbox" data-testid={`appr-role-${r.code}`} checked={config.gated_roles.includes(r.code)} onChange={() => toggleRole(r.code)} className="h-4 w-4 rounded border-slate-300" />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
          <div className="border-t border-slate-100 pt-4" data-testid="appr-matrix">
            <p className="mb-1 text-sm font-semibold text-slate-700">İşlem-Bazlı Onay Matrisi (Yetki Onay Kuyruğu)</p>
            <p className="mb-2 text-xs text-slate-500">Bir rol için işaretlenen işlem, o rol yaptığında onaya düşer (Big BOSS/amir onaylamadan uygulanmaz). İşaretsiz işlem doğrudan uygulanır. Matris boşsa yukarıdaki genel "onaya tabi roller" geçerlidir.</p>
            <select data-testid="appr-matrix-role" value={matrixRole || config.eligible_roles[0]?.code || ""} onChange={(e) => setMatrixRole(e.target.value)} className="mb-3 rounded-md border border-slate-300 px-3 py-1.5 text-sm">
              {config.eligible_roles.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
            <div className="grid gap-2 sm:grid-cols-2">
              {(config.actions || []).map((a) => {
                const role = matrixRole || config.eligible_roles[0]?.code;
                const checked = !!(config.action_matrix?.[role]?.[a.code]);
                return (
                  <label key={a.code} className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm ring-1 ring-inset ring-slate-200">
                    <input type="checkbox" data-testid={`appr-matrix-${role}-${a.code}`} checked={checked} onChange={() => toggleMatrix(role, a.code)} className="h-4 w-4 rounded border-slate-300" />
                    {a.label}
                  </label>
                );
              })}
            </div>
          </div>
          <Button data-testid="appr-save-config" onClick={saveConfig} disabled={busy === "cfg"} className="bg-brand-green text-white hover:bg-brand-green-600">{busy === "cfg" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Ayarları Kaydet</Button>
        </div>
        <WorkflowThresholdCard />
        <DiscountPolicyCard />
        <RefundPolicyCard />
        <DiscountHeatmapCard />
        <DelayedApprovalCard />
        </div>
      )}
    </div>
  );
}
